import { NavLink, Outlet } from "react-router-dom";
import { cn } from "@/design/cn";
import { useSeed, useAiFindings, useMateriality } from "@/domain/hooks";
import { deriveExceptions } from "@/domain/exceptions";

export type HubTab = { label: string; to: string; end?: boolean; count?: number };

/**
 * In-page tab navigation for a hub destination (IA redesign). The sidebar holds the hub;
 * its sibling surfaces are tabs here, not separate sidebar items. Keeps URLs deep-linkable.
 */
export function HubTabs({ tabs }: { tabs: HubTab[] }) {
  return (
    <div className="mb-5 flex flex-wrap gap-1 border-b border-line">
      {tabs.map((t) => (
        <NavLink
          key={t.to}
          to={t.to}
          end={t.end}
          className={({ isActive }) =>
            cn(
              "relative -mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors",
              isActive ? "border-brand text-ink" : "border-transparent text-mute hover:text-ink",
            )
          }
        >
          {t.label}
          {t.count != null && t.count > 0 && (
            <span className="rounded-full bg-subtle px-1.5 text-[11px] tabular-nums text-mute">{t.count}</span>
          )}
        </NavLink>
      ))}
    </div>
  );
}

export function PortfolioHub() {
  return (
    <>
      <HubTabs
        tabs={[
          { label: "Map", to: "/stratus/portfolio/map" },
          { label: "Site visits", to: "/stratus/portfolio/site-visits" },
        ]}
      />
      <Outlet />
    </>
  );
}

export function SignalsHub() {
  const seed = useSeed();
  const materiality = useMateriality();
  const findings = useAiFindings();
  const heimdallOpen = findings.filter((f) => f.status === "open").length;
  const leaksOpen = seed.leaks.filter((l) => l.state === "open" || l.state === "investigating").length;
  const exceptionsOpen = deriveExceptions(seed, materiality).length;
  return (
    <>
      <HubTabs
        tabs={[
          // TODO(heimdall): AI analyst not built yet — re-enable the Heimdall findings tab.
          // { label: "Heimdall findings", to: "/stratus/signals/heimdall", count: heimdallOpen },
          { label: "Exceptions", to: "/stratus/signals/exceptions", count: exceptionsOpen },
          { label: "Reconciliation", to: "/stratus/signals/leaks", count: leaksOpen },
        ]}
      />
      <Outlet />
    </>
  );
}

export function ControlsHub() {
  return (
    <>
      <HubTabs
        tabs={[
          { label: "Assurance", to: "/stratus/controls/assurance" },
          { label: "Compliance", to: "/stratus/controls/compliance" },
          { label: "Contractors & KYC", to: "/stratus/controls/contractors" },
          { label: "Roles & access", to: "/stratus/controls/access" },
        ]}
      />
      <Outlet />
    </>
  );
}

export function InsightsHub() {
  return (
    <>
      <HubTabs
        tabs={[
          { label: "Pace & outlook", to: "/stratus/insights/pace" },
          { label: "Comparison", to: "/stratus/insights/comparison" },
        ]}
      />
      <Outlet />
    </>
  );
}
